import React, { useRef, useEffect, useLayoutEffect, useState } from 'react'
import { EllipsisVerticalIcon as MoreIcon } from '@heroicons/react/24/outline'
import { TwButton } from '../Button/TwButton'

export interface DropdownMenuItem {
  label: string
  caption?: string
  icon: React.ReactNode
  onClick: () => void
  variant?: 'default' | 'danger'
}

interface DropdownMenuProps {
  items: DropdownMenuItem[]
  className?: string
  trigger?: React.ReactNode
  align?: 'left' | 'right'
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ items, className = '', trigger, align = 'left' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [resolvedAlign, setResolvedAlign] = useState(align)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // `align` is only a preference — flip it before paint when the panel would
  // leave the viewport (e.g. a left-aligned menu on a button at the right edge)
  useLayoutEffect(() => {
    if (!isOpen) return
    const rect = panelRef.current?.getBoundingClientRect()
    if (!rect) return
    const margin = 8
    if (align === 'left' && rect.right > window.innerWidth - margin) {
      setResolvedAlign('right')
    } else if (align === 'right' && rect.left < margin) {
      setResolvedAlign('left')
    } else {
      setResolvedAlign(align)
    }
  }, [isOpen, align])

  const handleItemClick = (item: DropdownMenuItem) => {
    item.onClick()
    setIsOpen(false)
  }

  // Separate danger items from regular items
  const regularItems = items.filter(item => item.variant !== 'danger')
  const dangerItems = items.filter(item => item.variant === 'danger')

  const alignmentClass =
    resolvedAlign === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {trigger ? (
        <div onClick={() => setIsOpen(!isOpen)}>
          {trigger}
        </div>
      ) : (
        <TwButton
          variant="outline"
          icon={<MoreIcon />}
          onClick={() => setIsOpen(!isOpen)}
        />
      )}

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown panel */}
          <div
            ref={panelRef}
            className={`absolute ${alignmentClass} z-50 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none divide-y divide-gray-100`}
          >
            {/* Regular items section */}
            {regularItems.length > 0 && (
              <div className="py-1">
                {regularItems.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="group flex w-full items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <span className="h-5 w-5 text-gray-400 group-hover:text-gray-500">
                      {item.icon}
                    </span>
                    <span className="flex flex-col items-start">
                      <span className="font-medium text-gray-900 group-hover:text-gray-900">
                        {item.label}
                      </span>
                      {item.caption && (
                        <span className="text-xs text-gray-500 group-hover:text-gray-600">
                          {item.caption}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            {/* Danger items section (separated) */}
            {dangerItems.length > 0 && (
              <div className="py-1">
                {dangerItems.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="group flex w-full items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <span className="h-5 w-5 text-red-400 group-hover:text-red-500">
                      {item.icon}
                    </span>
                    <span className="flex flex-col items-start">
                      <span className="font-medium text-red-600 group-hover:text-red-700">
                        {item.label}
                      </span>
                      {item.caption && (
                        <span className="text-xs text-red-500 group-hover:text-red-600">
                          {item.caption}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
